import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Users, BookOpen, Upload, Activity, TrendingUp, AlertCircle, CheckCircle, Clock, Database, FileText, Shield, LayoutDashboard, Mail, Globe, UserCheck } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { adminService } from '../../services/adminService';
import { KPICardSkeleton, TableSkeleton } from '../../components/ui/SkeletonLoader';
import NewsletterComposer from './components/NewsletterComposer';
import BibleLanguageManager from './components/BibleLanguageManager';
import SystemInfoPanel from './components/SystemInfoPanel';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalVerses: 0,
    totalBooks: 0,
    activeImports: 0,
    recentImports: 0
  });
  const [recentImports, setRecentImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();
  const { userProfile, isDemoAdmin, isAdmin: authAdmin } = useAuth();
  const [activeSection, setActiveSection] = useState('dashboard'); // 'dashboard' | 'newsletter' | 'languages' | 'system'

  useEffect(() => {
    checkAdminAccess();
  }, [userProfile, isDemoAdmin]);

  // COMPLETELY REVISED and BULLETPROOF admin access check
  const checkAdminAccess = async () => {
    try {
      setLoading(true);
      
      // PRIORITY 1: Check AuthContext admin status directly
      const contextIsAdmin = userProfile?.role === 'admin';
      const contextIsDemoAdmin = isDemoAdmin === true;
      
      if (contextIsAdmin || contextIsDemoAdmin) {
        console.log('Admin access granted via AuthContext:', { contextIsAdmin, contextIsDemoAdmin });
        setIsAdmin(true);
        await loadDashboardData();
        return;
      }
      
      // PRIORITY 2: Check adminService for edge cases (but don't block on it)
      try {
        const adminStatus = await adminService?.isAdmin();
        if (adminStatus) {
          console.log('Admin access granted via adminService');
          setIsAdmin(true);
          await loadDashboardData();
          return;
        }
      } catch (serviceError) {
        console.warn('AdminService check failed, falling back to context:', serviceError);
        // Don't fail here - context check above should be sufficient
      }
      
      // PRIORITY 3: Final fallback - check if we have any admin indicators
      if (userProfile?.email === 'admin@fortify.com') {
        console.log('Admin access granted via email fallback');
        setIsAdmin(true);
        await loadDashboardData();
        return;
      }
      
      // ACCESS DENIED - Clean error handling
      console.warn('Admin access denied - no admin privileges found');
      setError('Access denied. Administrator privileges required.');
      setLoading(false);
      setTimeout(() => navigate('/dashboard'), 2000);
      
    } catch (err) {
      console.error('Admin access check failed:', err);
      setError('Access verification failed. Redirecting to dashboard.');
      setLoading(false);
      setTimeout(() => navigate('/dashboard'), 2000);
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [dashboardStats, importHistory] = await Promise.all([
        adminService?.getDashboardStats(),
        adminService?.getImportHistory(5)
      ]);
      
      setStats(dashboardStats);
      setRecentImports(importHistory);
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Dashboard data error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-success" />;
      case 'processing':
        return <Clock className="w-4 h-4 text-primary" />;
      case 'failed':
        return <AlertCircle className="w-4 h-4 text-destructive" />;
      default:
        return <Clock className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // SIMPLIFIED: More reliable access denied screen
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <Shield className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">
            {error || 'Verifying Administrator Access...'}
          </h2>
          <p className="text-muted-foreground mb-4">
            {error ? 'You will be redirected shortly.' : 'Please wait while we verify your privileges.'}
          </p>
          
          {/* Show debug info for demo admin */}
          {isDemoAdmin && (
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-3 mb-4">
              <p className="text-sm text-primary font-medium">
                Demo Admin Session Active
              </p>
              <p className="text-xs text-primary/80 mt-1">
                Contact: admin@fortify.com
              </p>
            </div>
          )}
          
          {/* Loading indicator when no error */}
          {!error && (
            <div className="flex items-center justify-center mt-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          )}
          
          {/* User can manually navigate back */}
          <button 
            onClick={() => navigate('/dashboard')}
            className="mt-4 text-sm text-primary hover:text-primary/80 underline"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card shadow-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-4 sm:py-6 gap-3">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <Link
                  to="/dashboard"
                  className="flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 transition-colors"
                >
                  <LayoutDashboard className="w-4 h-4" />
                  <span>App Dashboard</span>
                </Link>
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-foreground">Admin Dashboard</h1>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm sm:text-base text-muted-foreground">
                  Fortified Mind Bible Study Administration
                </p>
                {isDemoAdmin && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                    <Shield className="w-3 h-3 mr-1" />
                    Demo Mode
                  </span>
                )}
              </div>
            </div>
            <Link
              to="/bible-version-import"
              className="inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2.5 rounded-lg transition-colors text-sm font-medium min-h-[44px] w-full sm:w-auto"
            >
              <Upload className="w-4 h-4" />
              Import Bible Version
            </Link>
          </div>

          {/* Section Tabs */}
          <div className="flex space-x-1 pb-0 -mb-px overflow-x-auto">
            <button
              onClick={() => setActiveSection('dashboard')}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeSection === 'dashboard' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            >
              <LayoutDashboard className="w-4 h-4 inline mr-1.5" />
              Overview
            </button>
            <button
              onClick={() => setActiveSection('newsletter')}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeSection === 'newsletter' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            >
              <Mail className="w-4 h-4 inline mr-1.5" />
              Newsletter
            </button>
            <button
              onClick={() => setActiveSection('subscribers')}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeSection === 'subscribers' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            >
              <UserCheck className="w-4 h-4 inline mr-1.5" />
              Subscribers
            </button>
            <button
              onClick={() => setActiveSection('languages')}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeSection === 'languages' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            >
              <Globe className="w-4 h-4 inline mr-1.5" />
              Languages
            </button>
            <button
              onClick={() => setActiveSection('system')}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeSection === 'system' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            >
              <Database className="w-4 h-4 inline mr-1.5" />
              System
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-destructive mr-2 shrink-0" />
              <span className="text-destructive text-sm">{error}</span>
            </div>
          </div>
        )}

        {/* Newsletter Section */}
        {activeSection === 'newsletter' && (
          <NewsletterComposer />
        )}

        {/* Languages Section */}
        {activeSection === 'languages' && (
          <BibleLanguageManager />
        )}

        {/* System Section */}
        {activeSection === 'system' && (
          <SystemInfoPanel />
        )}

        {/* Dashboard Section */}
        {activeSection === 'dashboard' && (
          <>
            {loading ? (
              <div className="space-y-6">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 lg:gap-6">
                  {[...Array(5)]?.map((_, i) => <KPICardSkeleton key={i} />)}
                </div>
                <TableSkeleton rows={5} cols={5} />
              </div>
            ) : (
              <>
                {/* Key Metrics */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
                  <div className="bg-card rounded-lg shadow-sm p-4 sm:p-6 border border-border">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs sm:text-sm font-medium text-muted-foreground">Total Users</p>
                        <p className="text-xl sm:text-2xl font-bold text-foreground">{stats?.totalUsers?.toLocaleString() || '0'}</p>
                      </div>
                      <div className="bg-primary/10 p-2 sm:p-3 rounded-full shrink-0">
                        <Users className="w-4 h-4 sm:w-6 sm:h-6 text-primary" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-card rounded-lg shadow-sm p-4 sm:p-6 border border-border">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs sm:text-sm font-medium text-muted-foreground">Bible Verses</p>
                        <p className="text-xl sm:text-2xl font-bold text-foreground">{stats?.totalVerses?.toLocaleString() || '0'}</p>
                      </div>
                      <div className="bg-success/10 p-2 sm:p-3 rounded-full shrink-0">
                        <FileText className="w-4 h-4 sm:w-6 sm:h-6 text-success" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-card rounded-lg shadow-sm p-4 sm:p-6 border border-border">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs sm:text-sm font-medium text-muted-foreground">Bible Books</p>
                        <p className="text-xl sm:text-2xl font-bold text-foreground">{stats?.totalBooks?.toLocaleString() || '0'}</p>
                      </div>
                      <div className="bg-accent/10 p-2 sm:p-3 rounded-full shrink-0">
                        <BookOpen className="w-4 h-4 sm:w-6 sm:h-6 text-accent" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-card rounded-lg shadow-sm p-4 sm:p-6 border border-border">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs sm:text-sm font-medium text-muted-foreground">Active Imports</p>
                        <p className="text-xl sm:text-2xl font-bold text-foreground">{stats?.activeImports?.toLocaleString() || '0'}</p>
                      </div>
                      <div className="bg-warning/10 p-2 sm:p-3 rounded-full shrink-0">
                        <Activity className="w-4 h-4 sm:w-6 sm:h-6 text-warning" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-card rounded-lg shadow-sm p-4 sm:p-6 border border-border col-span-2 sm:col-span-1">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs sm:text-sm font-medium text-muted-foreground">Recent Imports</p>
                        <p className="text-xl sm:text-2xl font-bold text-foreground">{stats?.recentImports?.toLocaleString() || '0'}</p>
                      </div>
                      <div className="bg-secondary p-2 sm:p-3 rounded-full shrink-0">
                        <TrendingUp className="w-4 h-4 sm:w-6 sm:h-6 text-secondary-foreground" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
                  <Link
                    to="/bible-version-import"
                    className="bg-card rounded-lg shadow-sm p-4 sm:p-6 hover:shadow-md hover:border-primary/40 transition-all border border-border min-h-[80px] flex flex-col justify-between"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="bg-primary/10 p-2.5 sm:p-3 rounded-full">
                        <Upload className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-base sm:text-lg font-semibold text-foreground mb-1">Import Bible Version</h3>
                      <p className="text-xs sm:text-sm text-muted-foreground">Upload and process new Bible translations</p>
                    </div>
                  </Link>

                  <Link
                    to="/bible-version-management"
                    className="bg-card rounded-lg shadow-sm p-4 sm:p-6 hover:shadow-md hover:border-success/40 transition-all border border-border min-h-[80px] flex flex-col justify-between"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="bg-success/10 p-2.5 sm:p-3 rounded-full">
                        <Database className="w-5 h-5 sm:w-6 sm:h-6 text-success" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-base sm:text-lg font-semibold text-foreground mb-1">Manage Bible Versions</h3>
                      <p className="text-xs sm:text-sm text-muted-foreground">View and manage imported Bible versions</p>
                    </div>
                  </Link>

                  <Link
                    to="/user-management"
                    className="bg-card rounded-lg shadow-sm p-4 sm:p-6 hover:shadow-md hover:border-accent/40 transition-all border border-border min-h-[80px] flex flex-col justify-between"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="bg-accent/10 p-2.5 sm:p-3 rounded-full">
                        <Users className="w-5 h-5 sm:w-6 sm:h-6 text-accent" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-base sm:text-lg font-semibold text-foreground mb-1">User Management</h3>
                      <p className="text-xs sm:text-sm text-muted-foreground">Manage user accounts and permissions</p>
                    </div>
                  </Link>

                  <Link
                    to="/reading-plan-management"
                    className="bg-card rounded-lg shadow-sm p-4 sm:p-6 hover:shadow-md hover:border-warning/40 transition-all border border-border min-h-[80px] flex flex-col justify-between"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="bg-warning/10 p-2.5 sm:p-3 rounded-full">
                        <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-warning" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-base sm:text-lg font-semibold text-foreground mb-1">Reading Plan Management</h3>
                      <p className="text-xs sm:text-sm text-muted-foreground">Create and manage Bible reading plans</p>
                    </div>
                  </Link>

                  <Link
                    to="/verse-list-management"
                    className="bg-card rounded-lg shadow-sm p-4 sm:p-6 hover:shadow-md hover:border-primary/40 transition-all border border-border min-h-[80px] flex flex-col justify-between"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="bg-primary/10 p-2.5 sm:p-3 rounded-full">
                        <span className="text-xl sm:text-2xl">📋</span>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-base sm:text-lg font-semibold text-foreground mb-1">Verse List Management</h3>
                      <p className="text-xs sm:text-sm text-muted-foreground">Create and manage curated verse lists by topic</p>
                    </div>
                  </Link>

                  <Link
                    to="/egw-plan-management"
                    className="bg-card rounded-lg shadow-sm p-4 sm:p-6 hover:shadow-md hover:border-accent/40 transition-all border border-border min-h-[80px] flex flex-col justify-between"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="bg-accent/10 p-2.5 sm:p-3 rounded-full">
                        <span className="text-xl sm:text-2xl">📖</span>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-base sm:text-lg font-semibold text-foreground mb-1">EGW Plan Management</h3>
                      <p className="text-xs sm:text-sm text-muted-foreground">Create and manage Ellen White reading plans</p>
                    </div>
                  </Link>

                  <Link
                    to="/admin-audit-log"
                    className="bg-card rounded-lg shadow-sm p-4 sm:p-6 hover:shadow-md hover:border-primary/40 transition-all border border-border min-h-[80px] flex flex-col justify-between"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="bg-primary/10 p-2.5 sm:p-3 rounded-full">
                        <Activity className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-base sm:text-lg font-semibold text-foreground mb-1">Audit Log</h3>
                      <p className="text-xs sm:text-sm text-muted-foreground">View all admin actions and changes</p>
                    </div>
                  </Link>
                </div>

                {/* Recent Import Activity */}
                <div className="bg-card rounded-lg shadow-sm p-4 sm:p-6 mb-4 sm:mb-6 border border-border">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-base sm:text-lg font-semibold text-foreground">Recent Import Activity</h2>
                    <Link
                      to="/bible-version-import"
                      className="text-primary hover:text-primary/80 text-sm font-medium"
                    >
                      View All
                    </Link>
                  </div>

                  {recentImports?.length === 0 ? (
                    <div className="text-center py-6">
                      <Upload className="w-10 h-10 sm:w-12 sm:h-12 text-muted-foreground/40 mx-auto mb-3" />
                      <p className="text-muted-foreground text-sm">No recent imports</p>
                      <p className="text-xs text-muted-foreground/60">Start by importing a Bible version</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {recentImports?.map((importItem) => (
                        <div key={importItem?.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 bg-muted/30 rounded-lg border border-border">
                          <div className="flex items-center space-x-3">
                            {getStatusIcon(importItem?.status)}
                            <div>
                              <p className="font-medium text-foreground text-sm">{importItem?.fileName}</p>
                              <p className="text-xs text-muted-foreground">
                                {formatFileSize(importItem?.fileSize)} • {importItem?.totalVerses} verses
                              </p>
                            </div>
                          </div>
                          <div className="text-left sm:text-right pl-7 sm:pl-0">
                            <p className="text-xs text-muted-foreground">{formatDate(importItem?.startedAt)}</p>
                            <p className="text-xs text-muted-foreground/70 capitalize">{importItem?.status}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* System Health */}
                <div className="bg-card rounded-lg shadow-sm p-4 sm:p-6 border border-border">
                  <h2 className="text-base sm:text-lg font-semibold text-foreground mb-4">System Health</h2>
                  <div className="grid grid-cols-3 gap-3 sm:gap-6">
                    <div className="text-center">
                      <div className="bg-success/10 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center mx-auto mb-2">
                        <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-success" />
                      </div>
                      <h3 className="font-medium text-foreground text-xs sm:text-sm">Database</h3>
                      <p className="text-xs text-success">Operational</p>
                    </div>

                    <div className="text-center">
                      <div className="bg-success/10 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center mx-auto mb-2">
                        <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-success" />
                      </div>
                      <h3 className="font-medium text-foreground text-xs sm:text-sm">File Storage</h3>
                      <p className="text-xs text-success">Operational</p>
                    </div>

                    <div className="text-center">
                      <div className="bg-success/10 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center mx-auto mb-2">
                        <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-success" />
                      </div>
                      <h3 className="font-medium text-foreground text-xs sm:text-sm">Authentication</h3>
                      <p className="text-xs text-success">Operational</p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;